import { ELEMENT_INFO, NEIGONG, type NeigongDef } from '@nine/shared';
import { bell, cloud, drop, flower, moon, swirl, taiji } from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';
import { defineIconKind, registerIcon } from '../store';

/**
 * Neigong books (กำลังภายใน): a thread-bound manual, its cover in the element's colour, a paper
 * title slip, the book's emblem, and its tier (ขั้น 1–6) as six studs along the foot — gold up to
 * the tier, dark after. School books have gold corner caps; jianghu books are plain.
 */

export const MAX_TIER = 6;

const rgb = (s: string): Hex => {
  const [r, g, b] = s.split(',').map((v) => Number(v.trim()));
  return `#${[r, g, b].map((v) => (v ?? 0).toString(16).padStart(2, '0')).join('')}`;
};

/** A neigong's own colour: its mastery aura's. */
export const neigongColor = (n: NeigongDef): Hex => rgb(n.aura.color);

/**
 * The emblem of a book, centred at (cx,cy), about `r` px in radius (5 on a 32 cover, works
 * down to 3). Shared with the passive statuses each book grants.
 */
export function neigongEmblem(p: Pix, id: string, cx: number, cy: number, r: number): void {
  const n = NEIGONG[id];
  const col = n ? neigongColor(n) : C.gold;
  switch (id) {
    case 'blood-oath':
    case 'ember-heart':
      drop(p, cx, cy + r * 0.35, r * 0.62, C.blood);
      if (id === 'ember-heart') p.line(cx - r, cy + r, cx + r, cy - r, C.steel);
      return;
    case 'cloud-heart':
    case 'cloud-void':
      cloud(p, cx - r, cy + r * 0.5, r * 2, id === 'cloud-void' ? C.white : '#c8e4ff');
      if (id === 'cloud-void') p.ring(cx, cy - r * 0.1, r * 0.55, r * 0.55, '#6a8ab0');
      return;
    case 'mist-breath':
      taiji(p, cx, cy, r);
      return;
    case 'mist-wheel':
      taiji(p, cx, cy, r * 0.75);
      for (let k = 0; k < 8; k++) {
        const a = (k * Math.PI) / 4;
        p.px(cx + Math.cos(a) * r, cy + Math.sin(a) * r, C.ink);
      }
      return;
    case 'bell-body':
    case 'bell-layers':
      bell(p, cx, cy - r * 0.55, Math.max(4, Math.round(r * 1.6)), C.gold, { w: r * 1.7 });
      if (id === 'bell-layers') p.rect(cx - r, cy + r - 1, r * 2, 1, C.paper);
      return;
    case 'road-iron':
    case 'lamp-spark':
      if (id === 'lamp-spark') {
        p.ell(cx, cy, r, r * 0.55, '#d6e0f0');
        p.poly(
          [
            [cx + r - 1, cy],
            [cx + r + 2, cy - r * 0.6],
            [cx + r + 2, cy + r * 0.6],
          ],
          '#d6e0f0',
        );
        p.px(cx - r * 0.5, cy - 1, C.ink);
      } else {
        p.line(cx - r, cy + r, cx + r, cy - r, C.steel).line(cx - r, cy - r, cx + r, cy + r, C.steel);
        p.px(cx - r, cy + r, C.gold).px(cx - r, cy - r, C.gold);
      }
      return;
    case 'mourning-heart':
    case 'vigil-heart':
      flower(p, cx, cy, r, id === 'vigil-heart' ? '#dc5ac8' : '#ff9ad8', C.gold);
      return;
    case 'ink-breath':
    case 'ink-stillness':
      p.ell(cx, cy, r, r, C.paper);
      p.ell(cx, cy + r * 0.3, r * 0.6, r * 0.6, C.ink);
      p.poly(
        [
          [cx - r * 0.5, cy + r * 0.1],
          [cx, cy - r],
          [cx + r * 0.5, cy + r * 0.1],
        ],
        C.ink,
      );
      if (id === 'ink-stillness') moon(p, cx + r * 0.4, cy - r * 0.4, r * 0.5, C.paper);
      return;
    case 'nine-heart':
      for (let k = 0; k < 5; k++) {
        const a = Math.PI + 0.4 + (k * (Math.PI - 0.8)) / 4;
        p.line(
          cx + Math.cos(a) * 1,
          cy + r * 0.6 + Math.sin(a) * 1,
          cx + Math.cos(a) * r,
          cy + r * 0.6 + Math.sin(a) * r * 1.4,
          k % 2 ? C.steel : C.gold,
        );
      }
      return;
    case 'ember-sea':
      swirl(p, cx, cy, r, C.blood, { arms: 2, turn: 3.6 });
      return;
    default:
      p.ell(cx, cy, r * 0.7, r * 0.7, col);
  }
}

/** The manual cover in the book's element, at 32. */
function book32(p: Pix, n: NeigongDef): void {
  const cover = mix(ELEMENT_INFO[n.element].color, '#24142c', 0.35);
  // Page block showing at the foot and fore-edge, then the cover over it.
  p.rect(7, 4, 19, 26, '#e8dcc4');
  p.rect(6, 3, 19, 26, cover);
  // Thread binding down the right edge: a darker strip and four stitches.
  p.rect(22, 3, 3, 26, mix(cover, '#24142c', 0.45));
  for (const y of [5, 11, 19, 26]) p.rect(21, y, 4, 1, C.paper);
  // The title slip.
  p.rect(8, 5, 4, 11, C.paper);
  p.rect(9, 7, 2, 1, C.ink).rect(9, 9, 2, 1, C.ink).rect(9, 11, 2, 2, C.ink);
  // The emblem in a lighter disc.
  p.ell(16, 15, 6, 6, mix(cover, C.paper, 0.35));
  neigongEmblem(p, n.id, 16, 15, 4.5);
  // Tier studs along the foot.
  // Tier studs: a dark plate at the foot with six sockets in two rows, gold up to the tier.
  p.rect(9, 22, 10, 7, '#2a1a24');
  for (let t = 0; t < MAX_TIER; t++)
    p.rect(10 + (t % 3) * 3, 23 + Math.floor(t / 3) * 3, 2, 2, t < n.tier ? C.goldLight : '#5a4650');
  if (n.category === 'school')
    for (const [x, y] of [
      [6, 3],
      [6, 27],
    ] as const)
      p.rect(x, y, 2, 2, C.gold);
}

/** The same book at 16, simplified: cover, binding, emblem colour, tier studs. */
function book16(p: Pix, n: NeigongDef): void {
  const cover = mix(ELEMENT_INFO[n.element].color, '#24142c', 0.35);
  p.rect(4, 2, 9, 13, '#e8dcc4');
  p.rect(3, 1, 9, 13, cover);
  p.rect(11, 1, 1, 13, mix(cover, '#24142c', 0.45));
  p.rect(4, 2, 2, 5, C.paper);
  neigongEmblem(p, n.id, 8, 7, 2.6);
  p.rect(4, 10, 7, 5, '#2a1a24');
  for (let t = 0; t < MAX_TIER; t++)
    p.px(5 + (t % 3) * 2, 11 + Math.floor(t / 3) * 2, t < n.tier ? C.goldLight : '#5a4650');
  if (n.category === 'school') p.px(3, 1, C.gold).px(3, 13, C.gold);
}

defineIconKind('neigong', {
  label: 'กำลังภายใน',
  ids: () => Object.keys(NEIGONG),
  name: (id) => NEIGONG[id]?.name ?? id,
  tint: (id) => (NEIGONG[id] ? ELEMENT_INFO[NEIGONG[id].element].color : C.gold),
  plate: 'none',
  fallback: () => ({ draw: (p) => p.rect(8, 4, 16, 24, C.wood) }),
});

for (const n of Object.values(NEIGONG))
  registerIcon('neigong', n.id, { draw: (p) => book32(p, n), draw16: (p) => book16(p, n) });
